import { AssetFilters, ImageFilter, defaultFilters, getDefaultAssetDateRange } from '../../components/assets/config';

export type AssetSortDirection = 'asc' | 'desc';

export interface AssetsInitialState {
  page: number;
  filters: AssetFilters;
  resultsPerPage: number;
  sortDirection: AssetSortDirection;
  compactRows: boolean;
}

const ASSET_NAV_STATE_KEY = 'assetsNavState';
const LEGACY_ASSET_NAV_STATE_KEY = 'vehiclesNavState';
const ASSET_RPP_KEY = 'assetsRowsPerPage';
const LEGACY_ASSET_RPP_KEY = 'vehiclesRowsPerPage';
const URL_KEY_SYSTEM = 'system';
const URL_KEY_PAGE = 'page';
const URL_KEY_RPP = 'rpp';
const URL_KEY_SORT = 'sort';
const URL_KEY_COMPACT = 'compact';
const URL_KEY_FILTERS = 'f';
const DEFAULT_RPP = 10;
const DEFAULT_SORT_DIRECTION: AssetSortDirection = 'desc';
const ALLOWED_RPP = [10, 25, 50, 100];

type ImageFilterKey =
  | 'croppedImages'
  | 'bgrImages'
  | 'exteriorImages'
  | 'featuredImages'
  | 'manualImages'
  | 'panoramicImages'
  | 'ucImages';

const IMAGE_FILTER_KEYS: ImageFilterKey[] = [
  'croppedImages',
  'bgrImages',
  'exteriorImages',
  'featuredImages',
  'manualImages',
  'panoramicImages',
  'ucImages',
];

const parseSafeJson = (value: string | null): any => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const cloneDefaultAssetFilters = (): AssetFilters => ({
  ...defaultFilters,
  croppedImages: { ...defaultFilters.croppedImages },
  bgrImages: { ...defaultFilters.bgrImages },
  exteriorImages: { ...defaultFilters.exteriorImages },
  featuredImages: { ...defaultFilters.featuredImages },
  manualImages: { ...defaultFilters.manualImages },
  panoramicImages: { ...defaultFilters.panoramicImages },
  ucImages: { ...defaultFilters.ucImages },
});

const parseImageFilter = (value: any, fallback: ImageFilter): ImageFilter => {
  if (!value || typeof value !== 'object') return fallback;
  return {
    checked: Boolean(value.checked),
    operator: typeof value.operator === 'string' ? value.operator : '=',
    imageCount: Number.isFinite(Number(value.imageCount)) ? Number(value.imageCount) : 0,
  };
};

const normalizeAssetFilters = (raw: any): AssetFilters => {
  const base = cloneDefaultAssetFilters();
  if (!raw || typeof raw !== 'object') return base;

  const next: AssetFilters = {
    ...base,
    ...raw,
    asset_id: typeof raw.asset_id === 'string' ? raw.asset_id : base.asset_id,
    start_date: typeof raw.start_date === 'string' ? raw.start_date : base.start_date,
    end_date: typeof raw.end_date === 'string' ? raw.end_date : base.end_date,
    configuration: typeof raw.configuration === 'string' ? raw.configuration : base.configuration,
    processingStatus: typeof raw.processingStatus === 'string' ? raw.processingStatus : base.processingStatus,
    syndicationStatus: typeof raw.syndicationStatus === 'string' ? raw.syndicationStatus : base.syndicationStatus,
    archived: Boolean(raw.archived),
  };

  IMAGE_FILTER_KEYS.forEach((key) => {
    next[key] = parseImageFilter(raw[key], base[key]);
  });

  return next;
};

const buildCompactAssetFilters = (filters: AssetFilters): Record<string, any> => {
  const compact: Record<string, any> = {};
  const defaultDates = getDefaultAssetDateRange();

  if (filters.asset_id) compact.asset_id = filters.asset_id;
  if (filters.start_date && filters.start_date !== defaultDates.start_date) compact.start_date = filters.start_date;
  if (filters.end_date && filters.end_date !== defaultDates.end_date) compact.end_date = filters.end_date;
  if (filters.configuration) compact.configuration = filters.configuration;
  if (filters.processingStatus && filters.processingStatus !== defaultFilters.processingStatus) compact.processingStatus = filters.processingStatus;
  if (filters.syndicationStatus && filters.syndicationStatus !== defaultFilters.syndicationStatus) compact.syndicationStatus = filters.syndicationStatus;
  if (filters.archived) compact.archived = true;

  IMAGE_FILTER_KEYS.forEach((key) => {
    const filter = filters[key];
    const isDefault =
      !filter?.checked &&
      (filter?.imageCount ?? 0) === 0 &&
      (filter?.operator || '=') === '=';
    if (!isDefault) compact[key] = filter;
  });

  return compact;
};

const readStoredResultsPerPage = (): number => {
  const stored = localStorage.getItem(ASSET_RPP_KEY) || localStorage.getItem(LEGACY_ASSET_RPP_KEY);
  const n = stored ? Number(stored) : NaN;
  return ALLOWED_RPP.includes(n) ? n : DEFAULT_RPP;
};

const defaultInitialState = (): AssetsInitialState => ({
  page: 1,
  filters: cloneDefaultAssetFilters(),
  resultsPerPage: readStoredResultsPerPage(),
  sortDirection: DEFAULT_SORT_DIRECTION,
  compactRows: false,
});

const readInitialStateFromUrl = (search: string): AssetsInitialState | null => {
  const params = new URLSearchParams(search);
  const hasShareState =
    params.has(URL_KEY_FILTERS) ||
    params.has(URL_KEY_PAGE) ||
    params.has(URL_KEY_RPP) ||
    params.has(URL_KEY_SORT) ||
    params.has(URL_KEY_COMPACT);

  if (!hasShareState) return null;

  const page = Number(params.get(URL_KEY_PAGE));
  const rpp = Number(params.get(URL_KEY_RPP));
  const sort = params.get(URL_KEY_SORT);
  const parsedFilters = parseSafeJson(params.get(URL_KEY_FILTERS)) || {};

  return {
    page: Number.isFinite(page) && page > 0 ? page : 1,
    filters: normalizeAssetFilters(parsedFilters),
    resultsPerPage: ALLOWED_RPP.includes(rpp) ? rpp : DEFAULT_RPP,
    sortDirection: sort === 'asc' ? 'asc' : DEFAULT_SORT_DIRECTION,
    compactRows: params.get(URL_KEY_COMPACT) === '1',
  };
};

const readInitialStateFromStorage = (): AssetsInitialState => {
  const stored = parseSafeJson(localStorage.getItem(ASSET_NAV_STATE_KEY) || localStorage.getItem(LEGACY_ASSET_NAV_STATE_KEY));
  if (!stored || typeof stored !== 'object') return defaultInitialState();

  return {
    page: Number.isFinite(Number(stored.page)) && Number(stored.page) > 0 ? Number(stored.page) : 1,
    filters: normalizeAssetFilters(stored.filters),
    resultsPerPage: ALLOWED_RPP.includes(Number(stored.resultsPerPage)) ? Number(stored.resultsPerPage) : readStoredResultsPerPage(),
    sortDirection: stored.sortDirection === 'asc' ? 'asc' : DEFAULT_SORT_DIRECTION,
    compactRows: Boolean(stored.compactRows),
  };
};

export const hasAssetShareParams = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return (
    params.has(URL_KEY_FILTERS) ||
    params.has(URL_KEY_PAGE) ||
    params.has(URL_KEY_RPP) ||
    params.has(URL_KEY_SORT) ||
    params.has(URL_KEY_COMPACT)
  );
};

export const readAssetsInitialState = (search: string): AssetsInitialState => {
  return readInitialStateFromUrl(search) || readInitialStateFromStorage();
};

export const saveAssetsNavState = (state: AssetsInitialState): void => {
  localStorage.setItem(ASSET_NAV_STATE_KEY, JSON.stringify(state));
  localStorage.setItem(ASSET_RPP_KEY, String(state.resultsPerPage));
};

export const buildAssetsSearchParams = (
  current: URLSearchParams,
  state: AssetsInitialState,
  systemCode?: string
): URLSearchParams => {
  const next = new URLSearchParams(current);
  const compactFilters = buildCompactAssetFilters(state.filters);

  if (systemCode) next.set(URL_KEY_SYSTEM, systemCode);
  else next.delete(URL_KEY_SYSTEM);

  next.set(URL_KEY_PAGE, String(state.page));
  next.set(URL_KEY_RPP, String(state.resultsPerPage));
  next.set(URL_KEY_SORT, state.sortDirection);
  if (state.compactRows) next.set(URL_KEY_COMPACT, '1');
  else next.delete(URL_KEY_COMPACT);

  if (Object.keys(compactFilters).length > 0) {
    next.set(URL_KEY_FILTERS, JSON.stringify(compactFilters));
  } else {
    next.delete(URL_KEY_FILTERS);
  }

  return next;
};
